<template>

    <modal
        name="extra-debt-payment-form"
        :width="820"
        height="auto"
        :adaptive="true"
        :scrollable="true"
        :click-to-close="!saving"
        @closed="resetForm"
    >

        <form
            class="modal-form payment-modal"
            @submit.prevent="savePayment"
        >

            <!-- =========================================
                 HEADER
            ========================================== -->

            <div class="modal-form-header">

                <div class="d-flex align-items-center min-width-0">

                    <span class="modal-title-icon">
                        <i class="fa-solid fa-wallet"></i>
                    </span>

                    <div class="min-width-0">

                        <h4 class="modal-title mb-0">
                            Registrar pago
                        </h4>

                        <small class="modal-subtitle d-block text-truncate">
                            Captura un pago parcial o liquida el saldo pendiente.
                        </small>

                    </div>

                </div>


                <button
                    type="button"
                    class="close ml-3"
                    :disabled="saving"
                    title="Cerrar"
                    aria-label="Cerrar formulario"
                    @click="closeModal"
                >
                    <span aria-hidden="true">&times;</span>
                </button>

            </div>


            <!-- =========================================
                 BODY
            ========================================== -->

            <div class="modal-form-body">

                <!-- LOADING -->

                <div
                    v-if="loadingSummary"
                    class="modal-loading"
                >

                    <span class="loading-icon">
                        <i class="fa-solid fa-circle-notch fa-spin"></i>
                    </span>

                    <strong>
                        Consultando adeudo
                    </strong>

                    <small class="text-muted">
                        Estamos validando el saldo disponible.
                    </small>

                </div>


                <template v-else>

                    <!-- ERROR -->

                    <div
                        v-if="formError"
                        class="alert alert-danger payment-alert"
                    >
                        <i class="fa-solid fa-circle-exclamation mr-1"></i>
                        {{ formError }}
                    </div>


                    <!-- =====================================
                         CONTEXTO DEL ADEUDO
                    ====================================== -->

                    <section class="debt-context">

                        <div class="debt-context-main">

                            <div class="debt-identification">

                                <span class="debt-id">
                                    #{{ debt ? debt.id : '-' }}
                                </span>

                                <span
                                    class="source-badge"
                                    :class="
                                        debtType === 'contract'
                                            ? 'source-badge--contract'
                                            : 'source-badge--without'
                                    "
                                >
                                    <i
                                        class="mr-1"
                                        :class="
                                            debtType === 'contract'
                                                ? 'fa-solid fa-file-contract'
                                                : 'fa-regular fa-file-lines'
                                        "
                                    ></i>

                                    {{
                                        debtType === 'contract'
                                            ? 'Con contrato'
                                            : 'Sin contrato'
                                    }}
                                </span>

                            </div>


                            <div class="debt-context-data">

                                <div
                                    v-if="propertyName"
                                    class="context-item"
                                >
                                    <i class="fa-regular fa-building"></i>

                                    <div>
                                        <small>
                                            Propiedad
                                        </small>

                                        <strong>
                                            {{ propertyName }}
                                        </strong>
                                    </div>
                                </div>


                                <div
                                    v-if="clientName"
                                    class="context-item"
                                >
                                    <i class="fa-regular fa-user"></i>

                                    <div>
                                        <small>
                                            Cliente
                                        </small>

                                        <strong>
                                            {{ clientName }}
                                        </strong>
                                    </div>
                                </div>


                                <div class="context-item">
                                    <i class="fa-regular fa-calendar"></i>

                                    <div>
                                        <small>
                                            Fecha del adeudo
                                        </small>

                                        <strong>
                                            {{ debtDate }}
                                        </strong>
                                    </div>
                                </div>

                            </div>

                        </div>


                        <!-- CONCEPTOS -->

                        <div
                            v-if="concepts.length"
                            class="concepts-panel"
                        >

                            <div class="concepts-header">

                                <span>
                                    <i class="fa-solid fa-list-check mr-1"></i>
                                    Conceptos
                                </span>

                                <small>
                                    {{ concepts.length }}
                                    {{
                                        concepts.length === 1
                                            ? 'concepto'
                                            : 'conceptos'
                                    }}
                                </small>

                            </div>


                            <div class="concepts-list">

                                <div
                                    v-for="(item, index) in concepts"
                                    :key="item.id || item.item_id || index"
                                    class="concept-row"
                                >

                                    <span class="concept-description">
                                        <span class="concept-index">
                                            {{ index + 1 }}.
                                        </span>

                                        {{ conceptDescription(item, index) }}
                                    </span>

                                    <strong>
                                        {{ money(conceptAmount(item)) }}
                                    </strong>

                                </div>

                            </div>

                        </div>

                    </section>


                    <!-- =====================================
                         RESUMEN FINANCIERO
                    ====================================== -->

                    <section class="payment-summary">

                        <div class="summary-card summary-total">

                            <span class="summary-icon">
                                <i class="fa-solid fa-file-invoice-dollar"></i>
                            </span>

                            <div>
                                <small>
                                    Total del adeudo
                                </small>

                                <strong>
                                    {{ money(summary.total) }}
                                </strong>
                            </div>

                        </div>


                        <div class="summary-card summary-paid">

                            <span class="summary-icon">
                                <i class="fa-solid fa-circle-check"></i>
                            </span>

                            <div>
                                <small>
                                    Total pagado
                                </small>

                                <strong>
                                    {{ money(summary.paid) }}
                                </strong>
                            </div>

                        </div>


                        <div class="summary-card summary-pending">

                            <span class="summary-icon">
                                <i class="fa-solid fa-hourglass-half"></i>
                            </span>

                            <div>
                                <small>
                                    Saldo pendiente
                                </small>

                                <strong>
                                    {{ money(summary.pending) }}
                                </strong>
                            </div>

                        </div>

                    </section>


                    <div class="progress-section">

                        <div class="progress-header">

                            <span>
                                Avance del pago
                            </span>

                            <strong>
                                {{ normalizedProgress }}%
                            </strong>

                        </div>


                        <div class="payment-progress">

                            <div
                                class="payment-progress-bar"
                                :class="progressClass"
                                :style="{
                                    width: normalizedProgress + '%'
                                }"
                            ></div>

                        </div>

                    </div>


                    <!-- =====================================
                         ADEUDO LIQUIDADO
                    ====================================== -->

                    <div
                        v-if="isDebtPaid"
                        class="paid-state"
                    >

                        <span class="paid-state-icon">
                            <i class="fa-solid fa-circle-check"></i>
                        </span>

                        <div>

                            <strong>
                                Adeudo liquidado
                            </strong>

                            <small>
                                No existe saldo pendiente para registrar otro pago.
                            </small>

                        </div>

                    </div>


                    <template v-else>

                        <!-- =================================
                             IMPORTE
                        ================================== -->

                        <section class="form-section">

                            <div class="section-heading">

                                <div>

                                    <span class="section-number">
                                        1
                                    </span>

                                    <div>
                                        <h5 class="form-section-title">
                                            Importe del pago
                                        </h5>

                                        <small>
                                            Selecciona cómo deseas registrar el movimiento.
                                        </small>
                                    </div>

                                </div>

                            </div>


                            <div class="payment-mode-selector">

                                <button
                                    type="button"
                                    class="payment-mode-card"
                                    :class="{
                                        active:
                                            paymentMode === 'full'
                                    }"
                                    :disabled="saving"
                                    @click="setPaymentMode('full')"
                                >

                                    <span class="payment-mode-check">
                                        <i
                                            :class="
                                                paymentMode === 'full'
                                                    ? 'fa-solid fa-circle-check'
                                                    : 'fa-regular fa-circle'
                                            "
                                        ></i>
                                    </span>


                                    <span class="payment-mode-copy">

                                        <strong>
                                            Liquidar saldo
                                        </strong>

                                        <small>
                                            El importe será igual al saldo pendiente.
                                        </small>

                                    </span>


                                    <strong class="payment-mode-value">
                                        {{ money(summary.pending) }}
                                    </strong>

                                </button>


                                <button
                                    type="button"
                                    class="payment-mode-card"
                                    :class="{
                                        active:
                                            paymentMode === 'partial'
                                    }"
                                    :disabled="saving"
                                    @click="setPaymentMode('partial')"
                                >

                                    <span class="payment-mode-check">
                                        <i
                                            :class="
                                                paymentMode === 'partial'
                                                    ? 'fa-solid fa-circle-check'
                                                    : 'fa-regular fa-circle'
                                            "
                                        ></i>
                                    </span>


                                    <span class="payment-mode-copy">

                                        <strong>
                                            Pago parcial
                                        </strong>

                                        <small>
                                            Captura un importe menor al saldo pendiente.
                                        </small>

                                    </span>

                                </button>

                            </div>


                            <div class="row mt-3">

                                <div class="col-md-6 mb-3 mb-md-0">

                                    <label class="required-label">
                                        Monto
                                    </label>


                                    <div class="input-group amount-input">

                                        <div class="input-group-prepend">

                                            <span class="input-group-text">
                                                $
                                            </span>

                                        </div>


                                        <input
                                            v-model="form.amount"
                                            type="number"
                                            min="0.01"
                                            step="0.01"
                                            class="form-control"
                                            :class="{
                                                'is-invalid':
                                                    fieldError('amount')
                                            }"
                                            :readonly="paymentMode === 'full'"
                                            :disabled="saving"
                                            placeholder="0.00"
                                        >

                                    </div>


                                    <small
                                        v-if="paymentMode === 'partial'"
                                        class="form-text text-muted"
                                    >
                                        Disponible para abonar:
                                        <strong>
                                            {{ money(summary.pending) }}
                                        </strong>
                                    </small>


                                    <div
                                        v-if="fieldError('amount')"
                                        class="invalid-feedback d-block"
                                    >
                                        {{ fieldError('amount') }}
                                    </div>

                                </div>


                                <div class="col-md-6">

                                    <label class="required-label">
                                        Fecha de pago
                                    </label>

                                    <input
                                        v-model="form.date"
                                        type="date"
                                        class="form-control"
                                        :class="{
                                            'is-invalid':
                                                fieldError('date')
                                        }"
                                        :disabled="saving"
                                    >

                                    <div
                                        v-if="fieldError('date')"
                                        class="invalid-feedback"
                                    >
                                        {{ fieldError('date') }}
                                    </div>

                                </div>

                            </div>

                        </section>


                        <!-- =================================
                             DATOS DE PAGO
                        ================================== -->

                        <section class="form-section">

                            <div class="section-heading">

                                <div>

                                    <span class="section-number">
                                        2
                                    </span>

                                    <div>
                                        <h5 class="form-section-title">
                                            Datos del movimiento
                                        </h5>

                                        <small>
                                            Registra cómo fue recibido el pago.
                                        </small>
                                    </div>

                                </div>

                            </div>


                            <div class="row">

                                <div class="col-md-6 mb-3">

                                    <label class="required-label">
                                        Forma de pago
                                    </label>

                                    <select
                                        v-model="form.type_payment_id"
                                        class="form-control"
                                        :class="{
                                            'is-invalid':
                                                fieldError('type_payment_id')
                                        }"
                                        :disabled="saving"
                                    >

                                        <option value="">
                                            Selecciona una opción
                                        </option>


                                        <option
                                            v-for="type in paymentTypes"
                                            :key="type.id"
                                            :value="type.id"
                                        >
                                            {{ type.name }}
                                        </option>

                                    </select>


                                    <div
                                        v-if="fieldError('type_payment_id')"
                                        class="invalid-feedback"
                                    >
                                        {{ fieldError('type_payment_id') }}
                                    </div>

                                </div>


                                <div class="col-md-6 mb-3">

                                    <label>
                                        Referencia
                                    </label>

                                    <input
                                        v-model.trim="form.reference"
                                        type="text"
                                        maxlength="100"
                                        class="form-control"
                                        :class="{
                                            'is-invalid':
                                                fieldError('reference')
                                        }"
                                        :disabled="saving"
                                        placeholder="Ej. SPEI-45872"
                                    >

                                    <div
                                        v-if="fieldError('reference')"
                                        class="invalid-feedback"
                                    >
                                        {{ fieldError('reference') }}
                                    </div>

                                </div>


                                <div class="col-12">

                                    <label>
                                        Descripción / notas
                                    </label>

                                    <textarea
                                        v-model.trim="form.description"
                                        rows="3"
                                        maxlength="3000"
                                        class="form-control"
                                        :class="{
                                            'is-invalid':
                                                fieldError('description')
                                        }"
                                        :disabled="saving"
                                        placeholder="Ej. Pago recibido por transferencia..."
                                    ></textarea>

                                    <div
                                        v-if="fieldError('description')"
                                        class="invalid-feedback"
                                    >
                                        {{ fieldError('description') }}
                                    </div>

                                </div>

                            </div>

                        </section>


                        <!-- =================================
                             CONFIRMACIÓN
                        ================================== -->

                        <section class="confirmation-panel">

                            <div class="confirmation-column">

                                <small>
                                    Pago a registrar
                                </small>

                                <strong class="confirmation-primary">
                                    {{ money(currentAmount) }}
                                </strong>

                            </div>


                            <div class="confirmation-divider">

                                <i class="fa-solid fa-arrow-right"></i>

                            </div>


                            <div class="confirmation-column">

                                <small>
                                    Saldo después del pago
                                </small>

                                <strong
                                    :class="{
                                        'text-success':
                                            balanceAfterPayment <= 0
                                    }"
                                >
                                    {{ money(balanceAfterPayment) }}
                                </strong>

                            </div>


                            <div class="confirmation-status">

                                <span
                                    class="confirmation-badge"
                                    :class="
                                        balanceAfterPayment <= 0
                                            ? 'confirmation-badge--paid'
                                            : 'confirmation-badge--partial'
                                    "
                                >
                                    <i
                                        class="mr-1"
                                        :class="
                                            balanceAfterPayment <= 0
                                                ? 'fa-solid fa-check'
                                                : 'fa-solid fa-layer-group'
                                        "
                                    ></i>

                                    {{
                                        balanceAfterPayment <= 0
                                            ? 'Quedará pagado'
                                            : 'Quedará parcial'
                                    }}
                                </span>

                            </div>

                        </section>

                    </template>

                </template>

            </div>


            <!-- =========================================
                 FOOTER
            ========================================== -->

            <div class="modal-form-footer">

                <button
                    type="button"
                    class="btn btn-sm btn-outline-secondary"
                    :disabled="saving"
                    @click="closeModal"
                >
                    Cancelar
                </button>


                <button
                    v-if="!isDebtPaid"
                    type="submit"
                    class="btn btn-sm btn-success"
                    :disabled="!canSave"
                >

                    <i
                        v-if="saving"
                        class="fa-solid fa-circle-notch fa-spin mr-1"
                    ></i>

                    <i
                        v-else
                        class="fa-solid fa-money-check-dollar mr-1"
                    ></i>

                    {{
                        saving
                            ? 'Registrando...'
                            : 'Registrar pago'
                    }}

                </button>


                <button
                    v-else
                    type="button"
                    class="btn btn-sm btn-secondary"
                    @click="closeModal"
                >
                    Cerrar
                </button>

            </div>

        </form>

    </modal>

</template>


<script>

import {
    mapActions
} from 'vuex';

import request
    from '@/services/request';

import Catalogue
    from '@/services/catalogue';


const EMPTY_SUMMARY = () => ({
    total: 0,
    paid: 0,
    pending: 0,
    progress: 0,
    status: 'pending',
    is_paid: false,
});


const EMPTY_FORM = () => ({
    date: '',
    amount: '',
    type_payment_id: '',
    reference: '',
    description: '',
});


export default {

    name:
        'ModalExtraDebtPaymentForm',


    data() {

        return {

            catalogue:
                new Catalogue(),

            debt:
                null,

            debtType:
                'without_contract',

            summary:
                EMPTY_SUMMARY(),

            form:
                EMPTY_FORM(),

            paymentMode:
                'full',

            loadingSummary:
                false,

            saving:
                false,

            errors:
                {},

            formError:
                '',

        };

    },


    computed: {

        paymentTypes() {

            return Array.isArray(
                this.catalogue.type_payments
            )
                ? this.catalogue.type_payments
                : [];

        },


        concepts() {

            if (!this.debt) {
                return [];
            }

            if (
                Array.isArray(
                    this.debt.due_items_extras
                ) &&
                this.debt.due_items_extras.length
            ) {
                return this.debt.due_items_extras;
            }

            if (
                Array.isArray(
                    this.debt.items
                ) &&
                this.debt.items.length
            ) {
                return this.debt.items;
            }

            if (
                Array.isArray(
                    this.debt.due_items_rent_only
                ) &&
                this.debt.due_items_rent_only.length
            ) {
                return this.debt.due_items_rent_only;
            }

            return [];

        },


        propertyName() {

            if (
                !this.debt ||
                !this.debt.contract ||
                !this.debt.contract.building
            ) {
                return '';
            }

            return this.debt
                .contract
                .building
                .name || '';

        },


        clientName() {

            if (!this.debt) {
                return '';
            }

            const user =
                this.debt.user ||
                (
                    this.debt.contract
                        ? this.debt.contract.user
                        : null
                );

            if (!user) {
                return '';
            }

            return [
                user.name,
                user.last_name,
                user.second_last_name,
            ]
                .filter(Boolean)
                .join(' ');

        },


        debtDate() {

            if (
                !this.debt ||
                !this.debt.due_date
            ) {
                return 'Sin fecha';
            }

            return this.formatDate(
                this.debt.due_date
            );

        },


        currentAmount() {

            const value =
                Number(
                    this.form.amount || 0
                );

            return Number.isFinite(value)
                ? value
                : 0;

        },


        balanceAfterPayment() {

            return Math.max(
                0,
                Number(
                    this.summary.pending || 0
                ) -
                this.currentAmount
            );

        },


        normalizedProgress() {

            return Math.max(
                0,
                Math.min(
                    100,
                    Number(
                        this.summary.progress || 0
                    )
                )
            );

        },


        progressClass() {

            if (this.isDebtPaid) {
                return 'payment-progress-bar--paid';
            }

            if (
                Number(
                    this.summary.paid || 0
                ) > 0
            ) {
                return 'payment-progress-bar--partial';
            }

            return 'payment-progress-bar--pending';

        },


        isDebtPaid() {

            return (
                this.summary.is_paid === true ||
                Number(
                    this.summary.pending || 0
                ) <= 0
            );

        },


        canSave() {

            if (
                this.saving ||
                this.loadingSummary ||
                this.isDebtPaid
            ) {
                return false;
            }

            const amount =
                this.currentAmount;

            const pending =
                Number(
                    this.summary.pending || 0
                );

            return (
                this.form.date !== '' &&
                this.form.type_payment_id !== '' &&
                amount > 0 &&
                amount <= pending
            );

        },

    },


    methods: {

        ...mapActions({

            errorNotification:
                'alert/error',

            successNotification:
                'alert/success',

        }),


        async openModal(buy) {

            this.resetForm();

            this.debt =
                buy;

            this.debtType =
                this.resolveDebtType(
                    buy
                );

            this.form.date =
                this.moment()
                    .format(
                        'YYYY-MM-DD'
                    );

            this.$modal.show(
                'extra-debt-payment-form'
            );


            await Promise.all([
                this.loadPaymentTypes(),
                this.loadSummary(),
            ]);

        },


        resolveDebtType(buy) {

            if (
                buy &&
                (
                    buy.source_type === 'contract' ||
                    buy.has_contract === true ||
                    buy.contract_id ||
                    buy.contract
                )
            ) {
                return 'contract';
            }

            return 'without_contract';

        },


        async loadPaymentTypes() {

            try {

                if (
                    typeof this.catalogue
                        .get_type_payments === 'function'
                ) {

                    await this.catalogue
                        .get_type_payments();

                    return;

                }


                if (
                    typeof this.catalogue
                        .get_types_payments === 'function'
                ) {

                    await this.catalogue
                        .get_types_payments();

                }

            } catch (error) {

                console.error(
                    'No se pudieron cargar las formas de pago.',
                    error
                );

            }

        },


        async loadSummary() {

            if (
                !this.debt ||
                !this.debt.id
            ) {
                return;
            }


            this.loadingSummary =
                true;

            this.formError =
                '';


            try {

                const response =
                    await request.get(
                        `/extra-debts/${this.debtType}/${this.debt.id}/payments/summary`
                    );


                const data =
                    response &&
                    response.data
                        ? response.data
                        : response;


                this.summary = {
                    ...EMPTY_SUMMARY(),
                    ...data,
                };


                if (
                    Number(
                        this.summary.pending || 0
                    ) > 0
                ) {

                    this.setPaymentMode(
                        'full'
                    );

                }

            } catch (error) {

                this.formError =
                    this.getErrorMessage(
                        error
                    );

            } finally {

                this.loadingSummary =
                    false;

            }

        },


        setPaymentMode(mode) {

            this.paymentMode =
                mode;

            this.errors =
                {};


            if (
                mode === 'full'
            ) {

                this.form.amount =
                    Number(
                        this.summary.pending || 0
                    )
                        .toFixed(2);

                return;

            }


            this.form.amount =
                '';

        },


        async savePayment() {

            if (!this.canSave) {
                return;
            }


            this.saving =
                true;

            this.errors =
                {};

            this.formError =
                '';


            try {

                const payload = {

                    date:
                        this.form.date,

                    amount:
                        Number(
                            this.form.amount
                        ),

                    type_payment_id:
                        Number(
                            this.form.type_payment_id
                        ),

                    reference:
                        this.form.reference || null,

                    description:
                        this.form.description || null,

                };


                const response =
                    await request.post(
                        `/extra-debts/${this.debtType}/${this.debt.id}/payments`,
                        payload
                    );


                const data =
                    response &&
                    response.data
                        ? response.data
                        : response;


                this.successNotification(
                    data.message ||
                    'El pago se registró correctamente.'
                );


                this.$modal.hide(
                    'extra-debt-payment-form'
                );


                Event.$emit(
                    'event-load-contract_dues'
                );

                Event.$emit(
                    'event-load-extra-dues-without-contract'
                );

                Event.$emit(
                    'event-extra-debt-payment-saved',
                    data
                );

            } catch (error) {

                this.setServerErrors(
                    error
                );


                if (
                    !Object.keys(
                        this.errors
                    ).length
                ) {

                    this.formError =
                        this.getErrorMessage(
                            error
                        );

                }

            } finally {

                this.saving =
                    false;

            }

        },


        conceptDescription(
            item,
            index
        ) {

            return (
                item.description ||
                item.name ||
                (
                    item.service
                        ? item.service.name
                        : ''
                ) ||
                `Concepto ${index + 1}`
            );

        },


        conceptAmount(item) {

            const amount =
                Number(
                    item.final_amount ??
                    item.total ??
                    item.amount ??
                    item.subtotal ??
                    item.base_amount ??
                    0
                );

            return item.negative
                ? -Math.abs(amount)
                : amount;

        },


        setServerErrors(error) {

            this.errors =
                {};


            if (
                error &&
                error.response &&
                error.response.data &&
                error.response.data.errors
            ) {

                this.errors =
                    error.response.data.errors;

            }

        },


        fieldError(field) {

            const error =
                this.errors[field];

            if (
                Array.isArray(error)
            ) {
                return error[0];
            }

            return error || '';

        },


        getErrorMessage(error) {

            if (
                error &&
                error.response &&
                error.response.data
            ) {

                return (
                    error.response.data.message ||
                    error.response.data.error ||
                    'No fue posible procesar la solicitud.'
                );

            }

            return 'No fue posible procesar la solicitud.';

        },


        closeModal() {

            if (this.saving) {
                return;
            }

            this.$modal.hide(
                'extra-debt-payment-form'
            );

        },


        resetForm() {

            this.debt =
                null;

            this.debtType =
                'without_contract';

            this.summary =
                EMPTY_SUMMARY();

            this.form =
                EMPTY_FORM();

            this.paymentMode =
                'full';

            this.loadingSummary =
                false;

            this.saving =
                false;

            this.errors =
                {};

            this.formError =
                '';

        },


        formatDate(value) {

            if (!value) {
                return 'Sin fecha';
            }

            const date =
                this.moment(
                    value
                );

            return date.isValid()
                ? date.format(
                    'DD/MM/YYYY'
                )
                : value;

        },


        money(value) {

            return new Intl.NumberFormat(
                'es-MX',
                {
                    style:
                        'currency',

                    currency:
                        'MXN',

                    minimumFractionDigits:
                        2,

                    maximumFractionDigits:
                        2,
                }
            ).format(
                Number(
                    value || 0
                )
            );

        },

    },


    created() {

        Event.$on(
            'event-open-extra-debt-payment-form',
            this.openModal
        );

    },


    beforeDestroy() {

        Event.$off(
            'event-open-extra-debt-payment-form',
            this.openModal
        );

    },

};

</script>


<style scoped>

/* =========================================
   BASE DEL MODAL
========================================= */

.payment-modal {
    background-color: #ffffff;
}

.min-width-0 {
    min-width: 0;
}

.modal-form-header {
    display: flex;

    align-items: center;
    justify-content: space-between;

    padding: 16px 20px;

    background-color: #ffffff;

    border-bottom:
        1px solid #e5e7eb;
}

.modal-title-icon {
    display: flex;

    align-items: center;
    justify-content: center;

    flex: 0 0 40px;

    width: 40px;
    height: 40px;

    margin-right: 11px;

    color: #596780;

    background-color: #f1f3f5;

    border-radius: 8px;
}

.modal-title {
    color: #343a40;

    font-size: 17px;
    font-weight: 600;
}

.modal-subtitle {
    margin-top: 2px;

    color: #7b8490;

    font-size: 11px;
}

.modal-form-body {
    max-height: 74vh;

    overflow-y: auto;

    padding: 18px 20px;
}

.modal-form-footer {
    display: flex;

    align-items: center;
    justify-content: flex-end;

    gap: 8px;

    padding: 13px 20px;

    background-color: #f8f9fa;

    border-top:
        1px solid #e5e7eb;
}


/* =========================================
   LOADING
========================================= */

.modal-loading {
    display: flex;

    align-items: center;
    justify-content: center;

    flex-direction: column;

    min-height: 270px;

    color: #59636e;
}

.loading-icon {
    display: flex;

    align-items: center;
    justify-content: center;

    width: 48px;
    height: 48px;

    margin-bottom: 8px;

    color: #596780;

    background-color: #f1f3f5;

    border-radius: 50%;

    font-size: 19px;
}

.modal-loading strong {
    margin-bottom: 2px;

    font-size: 13px;
}

.modal-loading small {
    font-size: 10px;
}

.payment-alert {
    margin-bottom: 14px;

    font-size: 12px;
}


/* =========================================
   CONTEXTO
========================================= */

.debt-context {
    padding: 13px;

    margin-bottom: 14px;

    background-color: #f8f9fa;

    border:
        1px solid #e3e6e9;

    border-radius: 8px;
}

.debt-context-main {
    display: flex;

    align-items: center;
    justify-content: space-between;

    gap: 15px;
}

.debt-identification {
    display: flex;

    align-items: center;

    gap: 7px;

    flex-shrink: 0;
}

.debt-id {
    color: #343a40;

    font-size: 16px;
    font-weight: 700;
}

.source-badge {
    display: inline-flex;

    align-items: center;

    padding: 4px 8px;

    border-radius: 999px;

    font-size: 9px;
    font-weight: 600;
}

.source-badge--contract {
    color: #1d4e89;

    background-color: #e9f0ff;
}

.source-badge--without {
    color: #59636e;

    background-color: #e9ecef;
}

.debt-context-data {
    display: flex;

    align-items: center;
    justify-content: flex-end;

    flex-wrap: wrap;

    gap: 13px;
}

.context-item {
    display: flex;

    align-items: center;

    gap: 7px;
}

.context-item > i {
    display: flex;

    align-items: center;
    justify-content: center;

    width: 28px;
    height: 28px;

    color: #7a8591;

    background-color: #ffffff;

    border:
        1px solid #e5e7eb;

    border-radius: 6px;
}

.context-item small {
    display: block;

    color: #8a939c;

    font-size: 8px;

    text-transform: uppercase;
}

.context-item strong {
    display: block;

    max-width: 180px;

    overflow: hidden;

    color: #495057;

    font-size: 10px;
    font-weight: 600;

    text-overflow: ellipsis;
    white-space: nowrap;
}


/* =========================================
   CONCEPTOS
========================================= */

.concepts-panel {
    margin-top: 11px;
    padding-top: 10px;

    border-top:
        1px solid #e3e6e9;
}

.concepts-header {
    display: flex;

    align-items: center;
    justify-content: space-between;

    margin-bottom: 5px;

    color: #6c757d;

    font-size: 9px;
    font-weight: 700;

    text-transform: uppercase;
}

.concepts-header small {
    color: #929aa3;

    font-size: 8px;

    text-transform: none;
}

.concept-row {
    display: flex;

    align-items: center;
    justify-content: space-between;

    gap: 10px;

    padding: 4px 0;

    border-bottom:
        1px solid #e9ecef;
}

.concept-row:last-child {
    border-bottom: 0;
}

.concept-description {
    min-width: 0;

    overflow: hidden;

    color: #495057;

    font-size: 10px;

    text-overflow: ellipsis;
    white-space: nowrap;
}

.concept-index {
    color: #9aa2aa;
}

.concept-row strong {
    flex-shrink: 0;

    color: #343a40;

    font-size: 10px;
}


/* =========================================
   RESUMEN FINANCIERO
========================================= */

.payment-summary {
    display: grid;

    grid-template-columns:
        repeat(
            3,
            minmax(
                0,
                1fr
            )
        );

    gap: 9px;

    margin-bottom: 9px;
}

.summary-card {
    display: flex;

    align-items: center;

    min-height: 74px;

    padding: 10px 11px;

    background-color: #ffffff;

    border:
        1px solid #e5e7eb;

    border-left:
        4px solid #6c757d;

    border-radius: 8px;

    box-shadow:
        0 1px 3px
        rgba(
            0,
            0,
            0,
            .03
        );
}

.summary-icon {
    display: flex;

    align-items: center;
    justify-content: center;

    flex: 0 0 34px;

    width: 34px;
    height: 34px;

    margin-right: 9px;

    color: #596780;

    background-color: #f1f3f5;

    border-radius: 50%;
}

.summary-card small {
    display: block;

    color: #8a939c;

    font-size: 8px;
    font-weight: 700;

    text-transform: uppercase;
}

.summary-card strong {
    display: block;

    color: #343a40;

    font-size: 14px;
    font-weight: 700;
}

.summary-total {
    border-left-color: #596780;
}

.summary-paid {
    border-left-color: #28a745;
}

.summary-paid strong {
    color: #218838;
}

.summary-pending {
    border-left-color: #ffc107;
}

.summary-pending strong {
    color: #856404;
}


/* =========================================
   PROGRESO
========================================= */

.progress-section {
    padding: 8px 11px;

    margin-bottom: 14px;

    background-color: #f8f9fa;

    border:
        1px solid #e5e7eb;

    border-radius: 7px;
}

.progress-header {
    display: flex;

    align-items: center;
    justify-content: space-between;

    margin-bottom: 5px;

    color: #6c757d;

    font-size: 9px;
    font-weight: 600;
}

.payment-progress {
    width: 100%;
    height: 7px;

    overflow: hidden;

    background-color: #e4e7eb;

    border-radius: 999px;
}

.payment-progress-bar {
    height: 100%;

    border-radius: 999px;

    transition:
        width .2s ease;
}

.payment-progress-bar--pending {
    background-color: #ffc107;
}

.payment-progress-bar--partial {
    background-color: #4b72d9;
}

.payment-progress-bar--paid {
    background-color: #28a745;
}


/* =========================================
   SECCIONES
========================================= */

.form-section {
    padding: 14px;

    margin-bottom: 13px;

    background-color: #ffffff;

    border:
        1px solid #e5e7eb;

    border-radius: 8px;
}

.section-heading {
    margin-bottom: 13px;
}

.section-heading > div {
    display: flex;

    align-items: center;
}

.section-number {
    display: flex;

    align-items: center;
    justify-content: center;

    flex: 0 0 27px;

    width: 27px;
    height: 27px;

    margin-right: 9px;

    color: #ffffff;

    background-color: #596780;

    border-radius: 50%;

    font-size: 10px;
    font-weight: 700;
}

.form-section-title {
    margin: 0;

    color: #495057;

    font-size: 12px;
    font-weight: 700;
}

.section-heading small {
    display: block;

    margin-top: 1px;

    color: #8a939c;

    font-size: 9px;
}

.form-section label {
    margin-bottom: 6px;

    color: #495057;

    font-size: 11px;
    font-weight: 600;
}

.required-label::after {
    content: ' *';

    color: #dc3545;
}

.form-control {
    min-height: 39px;

    font-size: 12px;

    border-radius: 6px;
}

textarea.form-control {
    min-height: 82px;
}

.form-text,
.invalid-feedback {
    font-size: 9px;
}


/* =========================================
   SELECCIÓN DE PAGO
========================================= */

.payment-mode-selector {
    display: grid;

    grid-template-columns:
        repeat(
            2,
            minmax(
                0,
                1fr
            )
        );

    gap: 9px;
}

.payment-mode-card {
    display: flex;

    align-items: center;

    min-height: 68px;

    padding: 10px 11px;

    color: #495057;

    background-color: #ffffff;

    border:
        1px solid #dfe3e7;

    border-radius: 8px;

    text-align: left;

    transition:
        border-color .15s ease,
        background-color .15s ease,
        box-shadow .15s ease;
}

.payment-mode-card:hover:not(:disabled) {
    border-color: #adb5bd;

    background-color: #fbfcfd;
}

.payment-mode-card.active {
    border-color: #4b72d9;

    background-color: #f5f8ff;

    box-shadow:
        inset 0 0 0 1px
        rgba(
            75,
            114,
            217,
            .08
        );
}

.payment-mode-card:disabled {
    cursor: not-allowed;

    opacity: .6;
}

.payment-mode-check {
    display: flex;

    align-items: center;
    justify-content: center;

    flex: 0 0 28px;

    width: 28px;
    height: 28px;

    margin-right: 8px;

    color: #9aa2aa;

    font-size: 15px;
}

.payment-mode-card.active
.payment-mode-check {
    color: #4b72d9;
}

.payment-mode-copy {
    min-width: 0;

    flex: 1;
}

.payment-mode-copy strong {
    display: block;

    color: #343a40;

    font-size: 11px;
    font-weight: 700;
}

.payment-mode-copy small {
    display: block;

    margin-top: 2px;

    color: #7b8490;

    font-size: 9px;
    line-height: 1.35;
}

.payment-mode-value {
    flex-shrink: 0;

    margin-left: 7px;

    color: #343a40;

    font-size: 11px;
}


/* =========================================
   MONTO
========================================= */

.amount-input .input-group-text {
    color: #59636e;

    background-color: #f1f3f5;

    font-size: 13px;
    font-weight: 700;
}

.amount-input .form-control {
    font-size: 15px;
    font-weight: 700;
}


/* =========================================
   CONFIRMACIÓN
========================================= */

.confirmation-panel {
    display: grid;

    grid-template-columns:
        minmax(
            0,
            1fr
        )
        auto
        minmax(
            0,
            1fr
        )
        auto;

    align-items: center;

    gap: 12px;

    padding: 12px 14px;

    background-color: #f8f9fa;

    border:
        1px solid #e5e7eb;

    border-radius: 8px;
}

.confirmation-column small {
    display: block;

    color: #8a939c;

    font-size: 8px;
    font-weight: 700;

    text-transform: uppercase;
}

.confirmation-column strong {
    display: block;

    margin-top: 1px;

    color: #343a40;

    font-size: 14px;
    font-weight: 700;
}

.confirmation-primary {
    color: #212529 !important;
}

.confirmation-divider {
    color: #adb5bd;

    font-size: 11px;
}

.confirmation-badge {
    display: inline-flex;

    align-items: center;

    padding: 5px 8px;

    border-radius: 999px;

    font-size: 9px;
    font-weight: 600;

    white-space: nowrap;
}

.confirmation-badge--partial {
    color: #1d4e89;

    background-color: #dbeafe;
}

.confirmation-badge--paid {
    color: #155724;

    background-color: #d4edda;
}


/* =========================================
   PAGADO
========================================= */

.paid-state {
    display: flex;

    align-items: center;

    padding: 14px;

    background-color: #f0f8f2;

    border:
        1px solid #cde7d2;

    border-radius: 8px;
}

.paid-state-icon {
    display: flex;

    align-items: center;
    justify-content: center;

    flex: 0 0 38px;

    width: 38px;
    height: 38px;

    margin-right: 10px;

    color: #218838;

    background-color: #ffffff;

    border-radius: 50%;
}

.paid-state strong {
    display: block;

    color: #155724;

    font-size: 12px;
}

.paid-state small {
    display: block;

    margin-top: 2px;

    color: #54705b;

    font-size: 9px;
}


/* =========================================
   RESPONSIVE
========================================= */

@media (max-width: 767px) {

    .modal-form-header,
    .modal-form-body,
    .modal-form-footer {
        padding-left: 14px;
        padding-right: 14px;
    }

    .debt-context-main {
        align-items: flex-start;

        flex-direction: column;
    }

    .debt-context-data {
        justify-content: flex-start;
    }

    .payment-summary {
        grid-template-columns: 1fr;
    }

    .payment-mode-selector {
        grid-template-columns: 1fr;
    }

    .confirmation-panel {
        grid-template-columns: 1fr;
    }

    .confirmation-divider {
        display: none;
    }

}

</style>
